/**
 * terrain — the English half, typed against the German module, so a
 * missing or misspelled key is a type error in this file.
 */
import type { terrain as de_terrain } from '../de/terrain';

export const terrain: Record<keyof typeof de_terrain, string> = {
  /* ---------------- terrain (terrain.ts, source 'terrain') ---------------- */
  'terrain.title': 'Terrain',
  'terrain.corridor': 'Corridor',
  'terrain.corridor.aria': 'Width of the checked corridor',
  'terrain.corridor.nm': '{nm} NM',
  'terrain.none': 'no terrain profile yet',
  'terrain.none.short': 'no suggestion',
  'terrain.kept': 'Safety altitude on leg {legs} was entered by hand and was not overwritten.',
  'terrain.leg': '{n} {from} → {to}',
  'terrain.max': 'terrain max {ft} ft at {nm} NM',
  'terrain.max.plain': 'terrain max {ft} ft',
  'terrain.obst': 'obstacle {name} {ft} ft',
  'terrain.obst.min': 'obstacle {name} ≥ {ft} ft',
  'terrain.obst.plain': 'obstacle {ft} ft',
  'terrain.obst.plain.min': 'obstacle ≥ {ft} ft',
  'obst.kind.chimney': 'chimney',
  'obst.kind.building': 'building',
  'obst.kind.wind_turbine': 'wind turbine',
  'obst.kind.tower': 'tower',
  'terrain.msa': 'safety altitude {ft} ft',
  'terrain.rule.flat': '+1000 ft',
  'terrain.rule.mountain': '+2000 ft over mountains',
  'terrain.nodata': 'no terrain data for this leg',
  'terrain.spark.aria': 'Terrain profile, leg {n}',
  'terrain.status.corridor': 'corridor {nm} NM',
  'terrain.hint':
    'Highest terrain + 1000 ft, over mountains + 2000 ft.',
  'terrain.field.derived': 'from the terrain along the route ({id} wide)',
  'terrain.field.elev': 'estimated from the terrain – no elevation is published for this aerodrome',

  /* ---------------- the cross-section of the whole route ---------------- */
  'terrain.chart.aria': 'Cross-section of the whole route, {nm} NM, scale up to {ft} ft',
  'terrain.head.highest': 'Highest point',
  'terrain.head.clearance': 'Terrain clearance',
  'terrain.head.noplan': 'no altitude planned yet',
  'terrain.head.conflict': 'First conflict',
  'terrain.head.conflict.at': 'leg {n}, {nm} NM',
  'terrain.legend.terrain': 'terrain',
  'terrain.legend.planned': 'planned altitude',
  'terrain.legend.safe': 'safety altitude',
  'terrain.legend.obst': 'obstacles',
  'terrain.col.suggest': 'Suggested',
  'terrain.col.planned': 'Planned',
  'terrain.planned.none': 'no altitude',
  'terrain.flag.low': 'below the suggestion',
  'terrain.flag.none': 'below the highest obstacle',
  'terrain.take': 'use it',
  'terrain.take.leg': 'Copy safety altitude {v} into leg {n}',
  'terrain.take.all': 'Use the suggestion on all {n} legs',
  'terrain.empty.title': 'No route yet.',
  'terrain.empty.line': 'Two points on the map are enough – the ground under the route appears here.',
  'terrain.error': 'Terrain is not reachable right now – it will be retried.',
  'terrain.caveat':
    'Advisory, not a clearance. Check AIP and NOTAM before the flight.',

  /* ---------------- airspace along the route ---------------- */
  'terrain.as.title': 'Airspace',
  'terrain.as.leg': 'Leg {n}',
  'terrain.as.class': 'Class {c}',
  'terrain.as.clear': 'No airspace is crossed at the planned altitude.',
  'terrain.as.show': 'on the map',
  'terrain.as.busy': 'checking …',
  'terrain.as.error': 'Airspace is not reachable right now – it will be retried.',

  /* The word beside the abbreviation. The abbreviation itself is what the chart
     prints and what is said on the radio, and it stays as it is. */
  'terrain.as.R': 'restricted area',
  'terrain.as.D': 'danger area',
  'terrain.as.P': 'prohibited area',
  'terrain.as.CTR': 'control zone',
  'terrain.as.MCTR': 'military control zone',
  'terrain.as.TMA': 'terminal control area',
  'terrain.as.CTA': 'control area',
  'terrain.as.ATZ': 'aerodrome traffic zone',
  'terrain.as.MATZ': 'military aerodrome traffic zone',
  'terrain.as.TMZ': 'transponder mandatory',
  'terrain.as.RMZ': 'radio mandatory',
  'terrain.as.TRA': 'temporarily reserved',
  'terrain.as.TSA': 'temporarily segregated',
  'terrain.as.ALERT': 'alert area',
  'terrain.as.WARNING': 'warning area',
  'terrain.as.PROTECTED': 'protected area',
  'terrain.as.GLIDING': 'gliding sector',
  'terrain.as.SPORT': 'air sports area',
  'terrain.as.HTZ': 'helicopter traffic zone',
  'terrain.as.TIZ': 'traffic information zone',
  'terrain.as.TIA': 'traffic information area',
  'terrain.as.AWY': 'airway',
  'terrain.as.ADIZ': 'air defence identification zone',

  /* ---------------- the notices at the top of the workspace ---------------- */
  'terrain.bn.clearance-none': 'Leg {leg}: planned altitude is below the highest obstacle.',
  'terrain.bn.clearance-low': 'Leg {leg}: planned altitude is below the suggested safety altitude.',
  'terrain.bn.airspace-restricted': 'The route crosses {n} area(s) with restrictions.',
  'terrain.bn.airspace-controlled': 'The route crosses {n} controlled airspaces at the planned altitude.',
};
